import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/**
 * The four languages the platform is translated into, matching the server's
 * `Language` enum and the `public/locales/*` directories. Declaring them is what
 * makes `i18n.resolvedLanguage` trustworthy: without `supportedLngs` the browser
 * detector hands through whatever the browser reports, so a visitor on `de-DE`
 * sends i18next after `/locales/de-DE/translation.json` and `/locales/de/…`,
 * gets two 404s, and only then falls back to English.
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'pt-br'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

i18n
  // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // learn more: https://github.com/i18next/i18next-http-backend
  .use(Backend)
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: 'en',
    debug: true,
    lowerCaseLng: true,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // `pt-br` is a supported language in its own right, not a region of some
    // `pt` we ship — so never strip the region to find a match.
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
  });

/**
 * The language code to send to the API, always one of `SUPPORTED_LANGUAGES`.
 *
 * `resolvedLanguage` rather than `language`: the latter is what was *asked for*
 * (possibly `de-DE`), the former what i18next settled on after `supportedLngs`
 * and `fallbackLng`. Sending the former would leave the UI in English while the
 * server answered in something else.
 */
export function apiLanguage(): SupportedLanguage {
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(resolved)
    ? (resolved as SupportedLanguage)
    : 'en';
}

export default i18n;
